import { customAlphabet } from 'nanoid';
import type { Account } from '../account';
import { MutableEntity } from '../common';
import { Organization } from './index';

/** Firebase SAML provider ID の suffix（saml. 以降）。先頭は小文字英字必須。 */
const firebaseProviderSlugGenerator = customAlphabet('abcdefghijklmnopqrstuvwxyz0123456789', 10);

export type _SamlConfig = MutableEntity & {
  organizationId: Organization.Id;
  firebaseProviderId: string;
  idpEntityId: string;
  ssoUrl: string;
  certificates: string[];
  enabled: boolean;
};

export namespace _SamlConfig {
  /**
   * SAML IdP 設定のうち「同一 IdP か」を判定するために必要な要素。
   * 証明書フォーマットの差異を吸収するため X.509 の SHA-256 フィンガープリントで比較する。
   */
  export type IdpConfigLike = {
    idpEntityId: string;
    ssoUrl: string;
    certificates: string[];
  };

  export type ConfigLike = IdpConfigLike & {
    firebaseProviderId: string;
    enabled: boolean;
  };

  export type Props = MutableEntity.Props & {
    organizationId: string;
    firebaseProviderId: string;
    idpEntityId: string;
    ssoUrl: string;
    certificates: string[];
    enabled: boolean;
  };

  export const Construct = (props: Props): _SamlConfig => {
    return {
      ...MutableEntity.Construct(props),
      organizationId: Organization.Id.FromString(props.organizationId),
      firebaseProviderId: props.firebaseProviderId,
      idpEntityId: props.idpEntityId,
      ssoUrl: props.ssoUrl,
      certificates: props.certificates,
      enabled: props.enabled,
    };
  };

  /**
   * Firebase Identity Platform 制約を満たす SAML provider ID を採番する。
   * - `saml.` プレフィックス
   * - suffix は小文字英字で開始、英数字で終了、2 文字以上
   */
  export const GenerateFirebaseProviderId = (): string => {
    let slug = firebaseProviderSlugGenerator();
    while (!/^[a-z]/.test(slug)) {
      slug = firebaseProviderSlugGenerator();
    }
    return `saml.${slug}`;
  };

  export namespace Create {
    export type Props = {
      idpEntityId: string;
      ssoUrl: string;
      certificates: string[];
      enabled: boolean;
    };
    export type Params = {
      operatorId: Account.Id;
      organizationId: Organization.Id;
      props: Props;
    };
  }
  export const Create = (params: Create.Params): _SamlConfig => {
    const { operatorId, organizationId, props } = params;
    return {
      ...MutableEntity.Create({ operatorId }),
      organizationId,
      firebaseProviderId: GenerateFirebaseProviderId(),
      idpEntityId: props.idpEntityId,
      ssoUrl: props.ssoUrl,
      certificates: props.certificates,
      enabled: props.enabled,
    };
  };

  export namespace Update {
    export type UpdatableKey = 'idpEntityId' | 'ssoUrl' | 'certificates' | 'enabled';
    export type UpdateProps = Partial<Pick<_SamlConfig, UpdatableKey>>;
    export type Params = {
      operatorId: Account.Id;
      cur: _SamlConfig;
      updateProps: UpdateProps;
    };
  }
  export const Update = ({ operatorId, cur, updateProps }: Update.Params): _SamlConfig => {
    return {
      ...cur,
      ...MutableEntity.Update({ cur, operatorId }),
      ...updateProps,
    };
  };
}
